/**
 * FormatEdit output カード向け: RT + workgate を1本のプレイリストとして扱う。
 * - 入場: ENTRY_N 件バッチ
 * - ボタン: BUTTON_N 件バッチ
 * - cursor を進めつつ循環（残り全件／wrap 全件はしない）
 */

/** output 入場時に自動表示する件数 */
export const RT_OUTPUT_ENTRY_AUTO_SHOW_COUNT = 2

/** 4ボタン押下1回あたりに表示する件数 */
export const RT_OUTPUT_BUTTON_SHOW_COUNT = 2

export type RtPlaylistPhase = 'entry' | 'button'

export function buildRtOutputPlaylist (
  matchedIndices: number[],
  dialogRows: unknown[],
  isWorkgateRow: (row: unknown) => boolean
): number[] {
  const playlist = Array.isArray(matchedIndices) ? matchedIndices.slice() : []
  const rows = Array.isArray(dialogRows) ? dialogRows : []
  for (let i = 0; i < rows.length; i++) {
    if (isWorkgateRow(rows[i]) && playlist.indexOf(i) < 0) {
      playlist.push(i)
    }
  }
  return playlist
}

/** cursor 位置の playlist 要素インデックス（循環） */
export function getPlaylistItemIndex (
  playlist: number[],
  cursor: number
): number | null {
  if (!Array.isArray(playlist) || playlist.length === 0) return null
  const c = Math.max(0, cursor | 0)
  return playlist[c % playlist.length]
}

/** 表示成功後の cursor 更新 */
export function advancePlaylistCursor (
  cursor: number,
  playlistLength: number,
  shown: number = 1
): number {
  if (playlistLength <= 0) return 0
  return Math.max(0, cursor | 0) + Math.max(0, shown | 0)
}

/**
 * バッチのチェーン上限。
 * playlist が空なら 0。それ以外は min(batchSize, length) ——
 * 循環表示なので batchSize を length で cap する（1周分を超える連続は不要）。
 */
export function getBatchDisplayMax (
  batchSize: number,
  playlistLength: number
): number {
  if (playlistLength <= 0) return 0
  const n = Math.max(0, batchSize | 0)
  if (n <= 0) return 0
  return Math.min(n, playlistLength)
}

/** @deprecated 互換: getBatchDisplayMax(entryCount, length) と同じ */
export function getEntryChainMax (
  playlistLength: number,
  entryCount: number = RT_OUTPUT_ENTRY_AUTO_SHOW_COUNT
): number {
  return getBatchDisplayMax(entryCount, playlistLength)
}

/**
 * 次のバッチで表示する playlist インデックス列（循環）。
 * 例: playlist=[0,1,2,3], cursor=3, batchSize=2 → [3,0]
 */
export function peekBatchPlaylistIndices (
  playlist: number[],
  cursor: number,
  batchSize: number
): number[] {
  const max = getBatchDisplayMax(batchSize, playlist.length)
  if (max <= 0) return []
  const out: number[] = []
  for (let i = 0; i < max; i++) {
    const idx = getPlaylistItemIndex(playlist, cursor + i)
    if (idx == null) break
    out.push(idx)
  }
  return out
}

function rowIdentityKey (row: unknown): string | null {
  if (!row || typeof row !== 'object') return null
  const r = row as { client_id?: unknown; permission_type?: unknown }
  if (r.client_id == null || r.permission_type == null) return null
  return `${String(r.client_id)}:${Number(r.permission_type)}`
}

/**
 * 条件行削除後に playlist の index を新 rows へ付け替える。
 * 消えた行は playlist から除外する。
 */
export function remapPlaylistAfterRowRemoval (
  oldPlaylist: number[],
  oldRows: unknown[],
  newRows: unknown[]
): number[] {
  if (!Array.isArray(oldPlaylist) || oldPlaylist.length === 0) return []
  const oldList = Array.isArray(oldRows) ? oldRows : []
  const newList = Array.isArray(newRows) ? newRows : []

  const newIndexByKey = new Map<string, number>()
  for (let i = 0; i < newList.length; i++) {
    const key = rowIdentityKey(newList[i])
    if (key != null && !newIndexByKey.has(key)) newIndexByKey.set(key, i)
  }

  const next: number[] = []
  const seen = new Set<number>()
  for (let i = 0; i < oldPlaylist.length; i++) {
    const oldIdx = oldPlaylist[i]
    const oldRow = oldList[oldIdx]
    const key = rowIdentityKey(oldRow)
    if (key == null) continue
    const newIdx = newIndexByKey.get(key)
    if (newIdx == null) continue
    if (seen.has(newIdx)) continue
    seen.add(newIdx)
    next.push(newIdx)
  }
  return next
}

/**
 * request_able === false の行 index を playlist から除外（防御）。
 */
export function prunePlaylistByRequestAble (
  playlist: number[],
  rows: unknown[]
): number[] {
  if (!Array.isArray(playlist) || playlist.length === 0) return []
  const list = Array.isArray(rows) ? rows : []
  return playlist.filter((idx) => {
    const row = list[idx] as { request_able?: boolean } | undefined
    if (!row || typeof row !== 'object') return false
    return row.request_able !== false
  })
}

/** playlist 長に合わせて cursor を clamp（空なら 0） */
export function clampPlaylistCursor (cursor: number, playlistLength: number): number {
  if (playlistLength <= 0) return 0
  const c = Math.max(0, cursor | 0)
  return Math.min(c, playlistLength)
}
